/**
 * Couleur et gras des deux messages d'une offre ou d'une facture : celui du
 * haut (« Message au client ») et celui du bas (« Message en bas du
 * document »).
 *
 * <p>Enregistrés ensemble, en une courte chaîne : « haut:rouge:g;bas:bleu ».
 * Seuls les noms de cette palette y entrent, jamais une couleur libre : le
 * document ne reçoit que des couleurs connues, bien lisibles à l'impression.
 */

/** Les couleurs proposées, dans l'ordre du choix. */
export const COULEURS_MESSAGE = [
  { cle: 'noir', nom: 'Noir', hex: '#0F172A' },
  { cle: 'marine', nom: 'Marine', hex: '#162035' },
  { cle: 'bleu', nom: 'Bleu', hex: '#1D4ED8' },
  { cle: 'turquoise', nom: 'Turquoise', hex: '#0E7490' },
  { cle: 'vert', nom: 'Vert', hex: '#0F6B3A' },
  { cle: 'rouge', nom: 'Rouge', hex: '#C0392B' },
  { cle: 'orange', nom: 'Orange', hex: '#C2410C' },
  { cle: 'or', nom: 'Or', hex: '#B45309' },
  { cle: 'violet', nom: 'Violet', hex: '#6D28D9' },
  { cle: 'bordeaux', nom: 'Bordeaux', hex: '#7F1D1D' },
];

const PAR_CLE = Object.fromEntries(COULEURS_MESSAGE.map((c) => [c.cle, c]));

/** Les deux messages qui portent une couleur. */
export const MESSAGES = ['haut', 'bas'];

/**
 * @param {string|null|undefined} texte « haut:rouge:g;bas:bleu »
 * @returns {{haut: {couleur: string|null, gras: boolean}, bas: {couleur: string|null, gras: boolean}}}
 *   le style de chaque message ; une couleur inconnue est ignorée
 */
export function lireStyles(texte) {
  const styles = { haut: { couleur: null, gras: false }, bas: { couleur: null, gras: false } };
  String(texte || '')
    .split(';')
    .forEach((morceau) => {
      const [quel, couleur, gras] = morceau.split(':');
      if (!MESSAGES.includes(quel)) return;
      styles[quel] = { couleur: PAR_CLE[couleur] ? couleur : null, gras: gras === 'g' };
    });
  return styles;
}

/**
 * @param {string|null|undefined} texte styles actuels
 * @param {'haut'|'bas'} quel message changé
 * @param {{couleur?: string|null, gras?: boolean}} style son nouveau style
 * @returns {string} les styles écrits, vide quand les deux messages sont
 *   ordinaires (noirs, sans gras)
 */
export function changeStyle(texte, quel, style) {
  const styles = lireStyles(texte);
  styles[quel] = { ...styles[quel], ...style };
  return MESSAGES.map((m) => {
    const { couleur, gras } = styles[m];
    const ordinaire = (!couleur || couleur === 'noir') && !gras;
    return ordinaire ? '' : m + ':' + (couleur || 'noir') + (gras ? ':g' : '');
  })
    .filter(Boolean)
    .join(';');
}

/**
 * @param {string|null|undefined} texte styles du document
 * @param {'haut'|'bas'} quel message affiché
 * @returns {{color?: string, fontWeight?: number}} le style à poser sur son texte
 */
export function styleDuMessage(texte, quel) {
  const { couleur, gras } = lireStyles(texte)[quel];
  const style = {};
  if (couleur) style.color = PAR_CLE[couleur].hex;
  if (gras) style.fontWeight = 700;
  return style;
}

/**
 * @param {string|null} cle couleur choisie
 * @returns {string} sa teinte ; noir sans choix
 */
export function teinte(cle) {
  return (PAR_CLE[cle] || PAR_CLE.noir).hex;
}
